/* eslint-disable @typescript-eslint/camelcase */
import Vue from "@/plugins"
import * as R from "ramda"
import { saveAs } from "file-saver"
import async from "async"
import { isEmpty, isArray, isArrayHasLength } from "@/utils/validate"
import convertArrayToCSV from "convert-array-to-csv"
import { notifyRequestError, notifySuccess } from "@/utils/notify"
import { AxiosError, AxiosResponse } from "axios"
import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import {
    SmartBox,
    AddSmartBox,
    DeleteSmartBox,
    UpdateSmartBox,
    UpdateLastSeenAtPayload,
    UpdateSbldriveVersionPayload,
    OperationLog,
    FuzzySearchParameter,
    FuzzySearchResult,
    TunnelRequest,
} from "@/models"
import {
    GET_FILTERED_SMART_BOX_LIST,
    GET_SMART_BOX_LIST,
    GET_SELECTED_SMART_BOX_LIST,
    GET_JWT_TOKEN,
    HAS_SELECTED_MARKET,
    HAS_SELECTED_VENUE,
    HAS_SELECTED_SMART_BOX,
    GET_SELECTED_MARKET,
    GET_SELECTED_VENUE,
    GET_USER_ID,
    GET_OPERATION_LOG_LIST,
    GET_SELECTED_VENUE_ID,
    GET_SELECTED_SMART_BOX_MAC_ADDRESS_LIST,
    GET_IS_SEARCH_MODE,
    GET_IS_FUZZY_SEARCH,
    GET_IS_ACCURATE_SEARCH,
    GET_FUZZY_SEARCH_PARAMETER,
} from "@/store/types/getters"
import {
    ADD_NEW_SMART_BOX,
    LOAD_SMARTBOX_LIST,
    LOAD_VENUE_LIST,
    LOAD_FUZZY_SEARCH_RESULT_LIST,
    LOAD_OPERATION_LOG_LIST,
    UPDATE_SELECTED_SMART_BOX_LIST,
    UPDATE_FUZZY_SEARCH_PARAMETER,
    APPEND_OPERATION_LOG,
    CLEAR_SMART_BOX_LIST,
    CLEAR_SELECTED_SMART_BOX_LIST,
    CLEAR_OPERATION_LOG_LIST,
    CLEAR_FUZZY_SEARCH_RESULT_MAP,
    CLEAR_FUZZY_SEARCH_PARAMETER,
} from "@/store/types/mutations"
import {
    fetchSmartBoxList,
    fuzzySearchSmartBoxList,
    addNewSmartBox,
    deleteSmartBox,
    updateSmartBox,
    deleteSmartBoxes,
    updateSmartBoxes,
    fetchOperationLogList,
    extractOperationLogsAsCsv,
    updateSmartBoxesLastSeenAt,
    updateSmartBoxesSbldriveVersions,
    openTunnel,
    closeTunnel,
} from "@/store/types/actions"
import { isNull, isUndefined } from "lodash"
import { MarketModuleNamespace, VenueModuleNamespace } from "@/consts"

export interface State {
    smartboxes: SmartBox[]
    selected: SmartBox[]
    logs: OperationLog[]
    fuzzySearchResultMap: { [key: string]: FuzzySearchResult }
    fuzzySearchParameter: FuzzySearchParameter
}

const state: State = {
    smartboxes: [],
    selected: [],
    logs: [],
    fuzzySearchResultMap: {},
    fuzzySearchParameter: {},
}

const namespaced = true

const getters: GetterTree<State, any> = {
    [GET_SELECTED_SMART_BOX_LIST](state: State): SmartBox[] {
        return state.selected
    },
    [GET_SELECTED_SMART_BOX_MAC_ADDRESS_LIST](state: State): string[] {
        return state.selected.map((x: SmartBox) => x.mac_address)
    },
    [HAS_SELECTED_SMART_BOX](state: State): boolean {
        return state.selected.length > 0
    },
    [GET_FILTERED_SMART_BOX_LIST](
        state: State,
        getters: any,
        _rootState: any,
        rootGetters: any
    ): SmartBox[] {
        return !getters[GET_IS_SEARCH_MODE]
            ? state.smartboxes
            : rootGetters[VenueModuleNamespace + "/" + HAS_SELECTED_VENUE] &&
              !isUndefined(
                  state.fuzzySearchResultMap[
                      rootGetters[
                          VenueModuleNamespace + "/" + GET_SELECTED_VENUE_ID
                      ]
                  ]
              )
            ? state.fuzzySearchResultMap[
                  rootGetters[
                      VenueModuleNamespace + "/" + GET_SELECTED_VENUE_ID
                  ]
              ].smartboxes
            : []
    },
    [GET_SMART_BOX_LIST](state: State, getters: any): SmartBox[] {
        return !getters[GET_IS_SEARCH_MODE]
            ? state.smartboxes
            : Array.prototype.concat.apply(
                  [],
                  Object.values(state.fuzzySearchResultMap).map(
                      (x: FuzzySearchResult) =>
                          x.smartboxes.map((y: SmartBox) => ({
                              ...y,
                              street: x.venue.street,
                              city: x.venue.city,
                              postcode: x.venue.postcode,
                          }))
                  )
              )
    },
    [GET_OPERATION_LOG_LIST](state: State): OperationLog[] {
        return state.logs
    },
    [GET_IS_SEARCH_MODE](state: State): boolean {
        return (
            !isUndefined(state.fuzzySearchParameter.fuzzy) ||
            !isUndefined(state.fuzzySearchParameter.accurate)
        )
    },
    [GET_IS_FUZZY_SEARCH](state: State): boolean {
        return !isUndefined(state.fuzzySearchParameter.fuzzy)
    },
    [GET_IS_ACCURATE_SEARCH](state: State): boolean {
        return !isUndefined(state.fuzzySearchParameter.accurate)
    },
    [GET_FUZZY_SEARCH_PARAMETER](state: State): FuzzySearchParameter {
        return state.fuzzySearchParameter
    },
}

const mutations: MutationTree<State> = {
    [LOAD_SMARTBOX_LIST](state: State, payload: SmartBox[]) {
        state.selected = []
        state.smartboxes = payload
    },
    [LOAD_OPERATION_LOG_LIST](state: State, payload: OperationLog[]) {
        state.logs = payload
    },
    [LOAD_FUZZY_SEARCH_RESULT_LIST](
        state: State,
        payload: { [key: string]: FuzzySearchResult }
    ) {
        state.fuzzySearchResultMap = payload
    },
    [UPDATE_FUZZY_SEARCH_PARAMETER](
        state: State,
        payload: FuzzySearchParameter
    ) {
        state.fuzzySearchParameter = payload
    },
    [ADD_NEW_SMART_BOX](state: State, payload: SmartBox) {
        state.smartboxes.push(payload)
    },
    [UPDATE_SELECTED_SMART_BOX_LIST](
        state: State,
        payload: SmartBox[] | null | undefined
    ) {
        if (!isArray(payload)) {
            return
        }
        state.selected = payload as SmartBox[]
    },
    [APPEND_OPERATION_LOG](state: State, { payload }) {
        if (!state.selected.length) {
            return
        }

        if (
            state.selected.some(
                (x: SmartBox) => x.mac_address === payload.mac_address
            )
        ) {
            if (state.logs.length >= 5) {
                state.logs.splice(0, 1)
            }

            state.logs.push(payload)
        }
    },
    [CLEAR_SMART_BOX_LIST](state: State) {
        state.selected = []
        state.smartboxes = []
    },
    [CLEAR_SELECTED_SMART_BOX_LIST](state: State) {
        state.selected = []
    },
    [CLEAR_OPERATION_LOG_LIST](state: State) {
        state.logs = []
    },
    [CLEAR_FUZZY_SEARCH_RESULT_MAP](state: State) {
        state.fuzzySearchResultMap = {}
    },
    [CLEAR_FUZZY_SEARCH_PARAMETER](state: State) {
        state.fuzzySearchParameter = {}
    },
}

const actions: ActionTree<State, any> = {
    [deleteSmartBox]({ state, rootGetters, getters }, payload: string) {
        let deleted = false

        if (!getters[GET_IS_SEARCH_MODE]) {
            const idx = state.smartboxes.findIndex(
                (x: SmartBox) => x.id === payload
            )
            if (idx > -1) {
                state.smartboxes.splice(idx, 1)
                deleted = true
            }
        } else if (
            rootGetters[VenueModuleNamespace + "/" + HAS_SELECTED_VENUE]
        ) {
            const selectedVenueId =
                rootGetters[VenueModuleNamespace + "/" + GET_SELECTED_VENUE_ID]
            if (!isUndefined(state.fuzzySearchResultMap[selectedVenueId])) {
                const idx = state.fuzzySearchResultMap[
                    selectedVenueId
                ].smartboxes.findIndex((x: SmartBox) => x.id === payload)
                if (idx > -1) {
                    state.fuzzySearchResultMap[
                        selectedVenueId
                    ].smartboxes.splice(idx, 1)
                    deleted = true
                }
            }
        }

        if (deleted && state.selected.length) {
            const idx = state.selected.findIndex(
                (x: SmartBox) => x.id === payload
            )
            if (idx > -1) {
                state.selected.splice(idx, 1)
            }
        }
    },
    [updateSmartBox]({ state, getters, rootGetters }, payload: SmartBox) {
        let newSmartBox: SmartBox | null = null

        if (!getters[GET_IS_SEARCH_MODE]) {
            const idx = state.smartboxes.findIndex(
                (x: SmartBox) => x.id == payload.id
            )
            if (idx > -1) {
                newSmartBox = Object.assign({}, state.smartboxes[idx], payload)

                state.smartboxes.splice(idx, 1, newSmartBox)
            }
        } else if (
            rootGetters[VenueModuleNamespace + "/" + HAS_SELECTED_VENUE]
        ) {
            const selectedVenueId =
                rootGetters[VenueModuleNamespace + "/" + GET_SELECTED_VENUE_ID]
            if (!isUndefined(state.fuzzySearchResultMap[selectedVenueId])) {
                const idx = state.fuzzySearchResultMap[
                    selectedVenueId
                ].smartboxes.findIndex((x: SmartBox) => x.id === payload.id)
                if (idx > -1) {
                    newSmartBox = Object.assign(
                        {},
                        state.fuzzySearchResultMap[selectedVenueId].smartboxes[
                            idx
                        ],
                        payload
                    )

                    state.fuzzySearchResultMap[
                        selectedVenueId
                    ].smartboxes.splice(idx, 1, newSmartBox)
                }
            }
        }

        if (!isNull(newSmartBox) && state.selected.length) {
            const idx = state.selected.findIndex(
                (x: SmartBox) => x.id === payload.id
            )
            if (idx > -1) {
                state.selected.splice(idx, 1, newSmartBox)
            }
        }
    },
    [updateSmartBoxesLastSeenAt]({ state, getters, rootGetters }, { payload }) {
        payload.forEach((element: UpdateLastSeenAtPayload) => {
            if (!getters[GET_IS_SEARCH_MODE]) {
                const idx = state.smartboxes.findIndex(
                    (x: SmartBox) => x.id === element.smartbox_id
                )
                if (idx > -1) {
                    Vue.set(
                        state.smartboxes[idx],
                        "last_seen_at",
                        element.last_seen_at
                    )
                    Vue.set(
                        state.smartboxes[idx],
                        "gateway_image_version",
                        element.gateway_image_version
                    )
                    Vue.set(
                        state.smartboxes[idx],
                        "port_allocated",
                        element.port_allocated
                    )
                }
            } else if (
                rootGetters[VenueModuleNamespace + "/" + HAS_SELECTED_VENUE]
            ) {
                const selectedVenueId =
                    rootGetters[
                        VenueModuleNamespace + "/" + GET_SELECTED_VENUE_ID
                    ]
                if (!isUndefined(state.fuzzySearchResultMap[selectedVenueId])) {
                    const idx = state.fuzzySearchResultMap[
                        selectedVenueId
                    ].smartboxes.findIndex(
                        (x: SmartBox) => x.id === element.smartbox_id
                    )
                    if (idx > -1) {
                        Vue.set(
                            state.fuzzySearchResultMap[selectedVenueId]
                                .smartboxes[idx],
                            "last_seen_at",
                            element.last_seen_at
                        )
                        Vue.set(
                            state.fuzzySearchResultMap[selectedVenueId]
                                .smartboxes[idx],
                            "gateway_image_version",
                            element.gateway_image_version
                        )
                        Vue.set(
                            state.fuzzySearchResultMap[selectedVenueId]
                                .smartboxes[idx],
                            "port_allocated",
                            element.port_allocated
                        )
                    }
                }
            }
        })
    },
    [updateSmartBoxesSbldriveVersions](
        { state, getters, rootGetters },
        { payload }
    ) {
        payload.forEach((element: UpdateSbldriveVersionPayload) => {
            if (!getters[GET_IS_SEARCH_MODE]) {
                const idx = state.smartboxes.findIndex(
                    (x: SmartBox) => x.id === element.smartbox_id
                )
                if (idx > -1) {
                    Vue.set(
                        state.smartboxes[idx],
                        "sbl_drive_version",
                        element.sbl_drive_version
                    )
                }
            } else if (
                rootGetters[VenueModuleNamespace + "/" + HAS_SELECTED_VENUE]
            ) {
                const selectedVenueId =
                    rootGetters[
                        VenueModuleNamespace + "/" + GET_SELECTED_VENUE_ID
                    ]
                if (!isUndefined(state.fuzzySearchResultMap[selectedVenueId])) {
                    const idx = state.fuzzySearchResultMap[
                        selectedVenueId
                    ].smartboxes.findIndex(
                        (x: SmartBox) => x.id === element.smartbox_id
                    )
                    if (idx > -1) {
                        Vue.set(
                            state.fuzzySearchResultMap[selectedVenueId]
                                .smartboxes[idx],
                            "sbl_drive_version",
                            element.sbl_drive_version
                        )
                    }
                }
            }
        })
    },
    [fetchOperationLogList]({ rootGetters, getters, commit }) {
        if (
            isEmpty(rootGetters[GET_JWT_TOKEN]) ||
            isNull(rootGetters[GET_USER_ID])
        ) {
            return
        }

        if (!getters[HAS_SELECTED_SMART_BOX]) {
            return commit(CLEAR_OPERATION_LOG_LIST)
        }

        const query = {
            size: 5,
            sort: [
                {
                    timestamp: "asc",
                },
            ],
            query: {
                bool: {
                    must: [
                        {
                            match_phrase: {
                                user_id: rootGetters[GET_USER_ID],
                            },
                        },
                    ],
                    should: getters[
                        GET_SELECTED_SMART_BOX_MAC_ADDRESS_LIST
                    ].map((mac_address: string) => ({
                        match_phrase: {
                            mac_address,
                        },
                    })),
                    minimum_should_match: 1,
                },
            },
        }

        Vue.prototype
            .$api({
                method: "POST",
                url: "/api/v1/analytics/logs",
                data: query,
                headers: {
                    Authorization: "Bearer " + rootGetters[GET_JWT_TOKEN],
                },
            })
            .then((resp: AxiosResponse<any>) => {
                if (
                    !isUndefined(resp.data) &&
                    !isUndefined(resp.data.hits) &&
                    isArrayHasLength(resp.data.hits.hits)
                ) {
                    commit(
                        LOAD_OPERATION_LOG_LIST,
                        resp.data.hits.hits.map(
                            (x: any) => x._source as OperationLog
                        )
                    )
                } else {
                    commit(LOAD_OPERATION_LOG_LIST, [])
                }
            })
            .catch((err: AxiosError) => {
                if (!isUndefined(err.response) && err.response.status === 404) {
                    return commit(LOAD_OPERATION_LOG_LIST, [])
                }

                notifyRequestError(err, "Failed to load operation logs")
            })
    },
    [fetchSmartBoxList]({ rootGetters, commit }) {
        if (
            isEmpty(rootGetters[GET_JWT_TOKEN]) ||
            !rootGetters[MarketModuleNamespace + "/" + HAS_SELECTED_MARKET] ||
            !rootGetters[VenueModuleNamespace + "/" + HAS_SELECTED_VENUE]
        ) {
            return
        }

        Vue.prototype
            .$api({
                method: "GET",
                url: `/api/v1/markets/${
                    rootGetters[
                        MarketModuleNamespace + "/" + GET_SELECTED_MARKET
                    ].id
                }/venues/${
                    rootGetters[VenueModuleNamespace + "/" + GET_SELECTED_VENUE]
                        .id
                }/smartboxes`,
                headers: {
                    Authorization: "Bearer " + rootGetters[GET_JWT_TOKEN],
                },
            })
            .then((resp: AxiosResponse<SmartBox[]>) => {
                commit(
                    LOAD_SMARTBOX_LIST,
                    resp.data.map((x: SmartBox) => ({
                        port_allocated: null,
                        is_opening_tunnel: false,
                        is_closing_tunnel: false,
                        ...x,
                    }))
                )
            })
            .catch((err: AxiosError) => {
                notifyRequestError(err, "Faied to fetch smartbox list")
            })
    },
    [fuzzySearchSmartBoxList](
        { rootGetters, commit },
        payload: FuzzySearchParameter
    ) {
        if (isEmpty(rootGetters[GET_JWT_TOKEN])) {
            return
        }

        commit(UPDATE_FUZZY_SEARCH_PARAMETER, payload)

        Vue.prototype
            .$api({
                method: "POST",
                url: "/api/v1/fuzzy-search",
                data: payload,
                headers: {
                    Authorization: "Bearer " + rootGetters[GET_JWT_TOKEN],
                },
            })
            .then(
                (resp: AxiosResponse<{ [key: string]: FuzzySearchResult }>) => {
                    const fuzzySearchResultList = resp.data

                    Object.keys(fuzzySearchResultList).forEach(
                        (venueId: string) => {
                            fuzzySearchResultList[
                                venueId
                            ].smartboxes = fuzzySearchResultList[
                                venueId
                            ].smartboxes.map((x: SmartBox) => ({
                                ...x,
                                port_allocated: null,
                            }))
                        }
                    )

                    commit(LOAD_FUZZY_SEARCH_RESULT_LIST, fuzzySearchResultList)

                    const values = Object.values(resp.data)
                    const venue_list = values.map(
                        (x: FuzzySearchResult) => x.venue
                    )
                    commit(
                        VenueModuleNamespace + "/" + LOAD_VENUE_LIST,
                        venue_list,
                        { root: true }
                    )
                }
            )
            .catch((err: AxiosError) => {
                notifyRequestError(err, "Faied to fuzzy search smartbox")
            })
    },
    [addNewSmartBox]({ rootGetters, getters, commit }, payload: AddSmartBox) {
        if (
            isEmpty(rootGetters[GET_JWT_TOKEN]) ||
            !rootGetters[VenueModuleNamespace + "/" + HAS_SELECTED_VENUE]
        ) {
            return
        }

        Vue.prototype
            .$api({
                method: "POST",
                url: `/api/v1/markets/${payload.market_id}/venues/${payload.venue_id}/smartboxes`,
                data: payload,
                headers: {
                    Authorization: "Bearer " + rootGetters[GET_JWT_TOKEN],
                },
            })
            .then((resp: AxiosResponse<SmartBox>) => {
                commit(ADD_NEW_SMART_BOX, {
                    port_allocated: null,
                    ...resp.data,
                })
                Vue.prototype.$modal.hide("SBLEBoxForm")
            })
            .catch((err: AxiosError) => {
                notifyRequestError(err, "Faied to add new smartbox")
            })
    },
    [openTunnel]({ rootGetters }, payload: TunnelRequest) {
        if (isEmpty(rootGetters[GET_JWT_TOKEN])) {
            return Promise.resolve()
        }

        return Vue.prototype
            .$api({
                method: "POST",
                url: "/api/v1/open-tunnel",
                data: payload,
                headers: {
                    Authorization: "Bearer " + rootGetters[GET_JWT_TOKEN],
                },
            })
            .then(() => {
                notifySuccess(
                    "Tunnel",
                    `Successfully opened tunnel to gateway: ${payload.gwMac}`
                )
            })
            .catch((err: AxiosError) => {
                notifyRequestError(err, "Faied to open tunnel")
            })
    },
    [closeTunnel]({ rootGetters }, payload: TunnelRequest) {
        if (isEmpty(rootGetters[GET_JWT_TOKEN])) {
            return Promise.resolve()
        }

        return Vue.prototype
            .$api({
                method: "POST",
                url: "/api/v1/close-tunnel",
                data: payload,
                headers: {
                    Authorization: "Bearer " + rootGetters[GET_JWT_TOKEN],
                },
            })
            .then(() => {
                notifySuccess(
                    "Tunnel",
                    `Successfully closed tunnel to gateway: ${payload.gwMac}`
                )
            })
            .catch((err: AxiosError) => {
                notifyRequestError(err, "Faied to close tunnel")
            })
    },
    [deleteSmartBoxes]({ rootGetters, dispatch }, payload: DeleteSmartBox[]) {
        if (isEmpty(rootGetters[GET_JWT_TOKEN])) {
            return
        }

        async.series(
            payload.map((x: DeleteSmartBox) => {
                return (cb: any) => {
                    return Vue.prototype
                        .$api({
                            method: "DELETE",
                            url: `/api/v1/markets/${x.market_id}/venues/${x.venue_id}/smartboxes/${x.id}`,
                            headers: {
                                Authorization:
                                    "Bearer " + rootGetters[GET_JWT_TOKEN],
                            },
                        })
                        .then((resp: AxiosResponse<SmartBox>) => {
                            dispatch(deleteSmartBox, resp.data.id)
                            cb(null)
                        })
                        .catch((err: AxiosError) => {
                            cb(err)
                        })
                }
            }),
            (err: AxiosError | null) => {
                if (!isNull(err)) {
                    notifyRequestError(err, "Faied to delete certain smartbox")
                } else {
                    Vue.prototype.$modal.hide("SBLEBoxForm")
                }
            }
        )
    },
    [updateSmartBoxes]({ rootGetters, dispatch }, payload: UpdateSmartBox[]) {
        if (isEmpty(rootGetters[GET_JWT_TOKEN])) {
            return Promise.reject()
        }

        return new Promise((resolve, reject) => {
            async.series(
                payload.map((x: UpdateSmartBox) => {
                    return (cb: any) => {
                        return Vue.prototype
                            .$api({
                                method: "PUT",
                                url: `/api/v1/markets/${x.market_id}/venues/${x.venue_id}/smartboxes/${x.id}`,
                                data: x,
                                headers: {
                                    Authorization:
                                        "Bearer " + rootGetters[GET_JWT_TOKEN],
                                },
                            })
                            .then((resp: AxiosResponse<SmartBox>) => {
                                dispatch(updateSmartBox, resp.data)
                                cb(null)
                            })
                            .catch((err: AxiosError) => {
                                cb(err)
                            })
                    }
                }),
                (err: AxiosError | null) => {
                    if (!isNull(err)) {
                        notifyRequestError(
                            err,
                            "Faied to update certain smartbox"
                        )
                        reject()
                    } else {
                        resolve()
                    }
                }
            )
        })
    },
    [extractOperationLogsAsCsv]({ rootGetters, getters }) {
        if (
            isEmpty(rootGetters[GET_JWT_TOKEN]) ||
            isNull(rootGetters[GET_USER_ID])
        ) {
            return
        }

        const query: any = {
            sort: [
                {
                    timestamp: "desc",
                },
            ],
            size: -1,
            query: {
                bool: {
                    must: [
                        {
                            match_phrase: {
                                user_id: rootGetters[GET_USER_ID],
                            },
                        },
                    ],
                    should: getters[
                        GET_SELECTED_SMART_BOX_MAC_ADDRESS_LIST
                    ].map((mac_address: string) => ({
                        match_phrase: {
                            mac_address,
                        },
                    })),
                    minimum_should_match: 1,
                },
            },
        }

        Vue.prototype
            .$api({
                method: "POST",
                url: "/api/v1/analytics/logs",
                data: query,
                headers: {
                    Authorization: "Bearer " + rootGetters[GET_JWT_TOKEN],
                },
            })
            .then((resp: AxiosResponse<OperationLog[]>) => {
                const logs = resp.data.map(
                    (x: any) => x._source as OperationLog
                )
                const filename = "logs.csv"
                const blob = new Blob(
                    [
                        convertArrayToCSV(
                            logs.map((x: OperationLog) => ({
                                ...R.pick(
                                    [
                                        "timestamp",
                                        "ty",
                                        "message",
                                        "mac_address",
                                    ],
                                    x
                                ),
                                timestamp: Vue.prototype.$F.formatDateTime(
                                    x.timestamp
                                ),
                            }))
                        ),
                    ],
                    {
                        type: "text/csv; charset=utf-8",
                    }
                )

                saveAs(blob, filename)
            })
            .catch((err: AxiosError) => {
                notifyRequestError(err, "Faied to download logs")
            })
    },
}

const module: Module<State, any> = {
    namespaced,
    state,
    getters,
    mutations,
    actions,
}

export default module
